import React from 'react'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
} from '@mui/material'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { useTranslation } from 'react-i18next'

import { FolderOutput } from '@core/api/generated'
import FTGTooltip from '@core/components/FTGTooltip'

const styles = {
  inputV2: {
    backgroundColor: (theme) => theme.palette.color.N010,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: '1px solid #e1e3e8',
      },
      '&:hover fieldset': {
        border: '1px solid #e1e3e8',
      },
      '&.Mui-focused fieldset': {
        border: '1px solid #e1e3e8',
      },
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: '0.875rem',
      fontWeight: 500,
      opacity: 1,
    },
  },
  autocompleteV2: {
    '& .MuiAutocomplete-paper': {
      maxHeight: 40,
    },
  },
}
type FoldersTreeAutocompleteProps = {
  isLoading?: boolean
  folders: FolderOutput[]
  value: FolderOutput | FolderOutput[] | null
  onChange: (value: FolderOutput | FolderOutput[] | null) => void
  label?: string
  currentFolderId?: string | null
  mode: 'save' | 'move'
  vivid?: boolean
  multiple?: boolean
  placeholder?: string
  showSelectWithChildrenAction?: boolean
  onSelectWithChildren?: (folder: FolderOutput) => void
  onDeselectWithChildren?: (folder: FolderOutput) => void
}

const MAX_VISIBLE_TAGS = 1

const searchOptions = {
  requireMatchAll: true,
  insideWords: true,
  findAllOccurrences: true,
}

const FoldersTreeAutocomplete = (props: FoldersTreeAutocompleteProps) => {
  const { t } = useTranslation('folders', { keyPrefix: 'autocomplete' })
  const [inputValue, setInputValue] = React.useState('')
  const selectedFolders = React.useMemo(
    () => (props.multiple && Array.isArray(props.value) ? props.value : []),
    [props.multiple, props.value],
  )

  return (
    <Autocomplete
      loading={props.isLoading}
      inputValue={inputValue}
      filterOptions={(options) => {
        if (!inputValue.trim()) {
          return options
        }

        const matchingIds = new Set()

        options.forEach((option) => {
          const isMatch = match(option.name, inputValue, searchOptions).length > 0

          if (isMatch) {
            option.breadcrumbs.forEach((breadcrumb) => {
              matchingIds.add(breadcrumb.id)
            })
          }
        })

        const filtered = options.filter((option) => {
          if (matchingIds.has(option.id)) return true

          return false
        })

        return filtered
      }}
      getOptionDisabled={(option) => {
        if (props.mode === 'move' && option.id === props.currentFolderId) {
          return true
        }

        return !option.permissions?.write
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue)
      }}
      options={props.folders}
      multiple={props.multiple}
      size="small"
      value={props.value}
      onChange={(_, value) => {
        if (props.multiple) {
          props.onChange(value as FolderOutput[])
          return
        }

        props.onChange((value as FolderOutput | null) || null)
      }}
      fullWidth
      noOptionsText={t('no_folders')}
      ListboxProps={{
        sx: {
          overflowX: 'hidden',
        },
      }}
      renderTags={
        props.multiple
          ? (value, getTagProps) => {
              const visibleTags = value.slice(0, MAX_VISIBLE_TAGS)
              const hiddenCount = value.length - visibleTags.length

              return [
                ...visibleTags.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                  />
                )),
                ...(hiddenCount > 0
                  ? [
                      <Chip
                        key="more-selected-folders"
                        label={t('more_selected', {
                          count: hiddenCount,
                        })}
                        size="small"
                        disabled
                      />,
                    ]
                  : []),
              ]
            }
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label || t('select_folder')}
          fullWidth
          sx={props.vivid ? undefined : styles.inputV2}
          placeholder={props.multiple ? props.placeholder : t('type_to_search')}
        />
      )}
      getOptionLabel={(option) => {
        return option.name
      }}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderOption={(optionProps, option) => {
        const isSelected = Boolean(optionProps['aria-selected'] === 'true')
        const isSubtreeSelected = selectedFolders.some(
          (selectedFolder) =>
            selectedFolder.id === option.id ||
            selectedFolder.breadcrumbs.some((breadcrumb) => breadcrumb.id === option.id),
        )
        const matches = match(option.name, inputValue, searchOptions)
        const parts: { highlight: boolean; text: string }[] = parse(option.name, matches)
        const selectChildrenActionTitle = isSubtreeSelected
          ? t('deselect_with_children')
          : t('select_with_children')

        let secondaryText = ''

        if (props.mode === 'move' && option.id === props.currentFolderId) {
          secondaryText = t('cannot_move_same_folder')
        } else if (!option.permissions?.write) {
          secondaryText = t('no_access_folder')
        }

        return (
          <ListItem
            {...optionProps}
            key={option.id}
            dense
            aria-label={option.name}
            sx={{
              my: 0.25,
              borderRadius: 1.5,
              alignItems: 'center',
              overflow: 'hidden',
              ...(isSelected && {
                backgroundColor: 'secondary.light',
                '&:hover': {
                  backgroundColor: 'secondary.light',
                },
              }),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
              <ListItemText
                id={option.id}
                color="text.primary"
                secondaryTypographyProps={{
                  color: isSelected ? 'secondary.dark' : 'primary.main',
                  fontWeight: 600,
                }}
                sx={{
                  minWidth: 0,
                  pr:
                    props.showSelectWithChildrenAction &&
                    props.onSelectWithChildren &&
                    option.children &&
                    option.children.length > 0
                      ? 1
                      : 0,
                  pl: (option.breadcrumbs.length - 1) * 2,
                }}
                primary={
                  <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    {option.breadcrumbs.length > 1 && (
                      <KeyboardArrowUpIcon sx={{ color: isSelected ? 'secondary.main' : '' }} />
                    )}
                    <FTGTooltip
                      title={option.name}
                      disabled={option.name.length <= 50}
                      placement="top"
                    >
                      <Box
                        component="span"
                        sx={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: isSelected ? 'secondary.dark' : 'text.primary',
                        }}
                      >
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.highlight || isSelected ? 700 : 500,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </Box>
                    </FTGTooltip>
                  </Box>
                }
                secondary={secondaryText}
              />
              {props.showSelectWithChildrenAction &&
              props.onSelectWithChildren &&
              option.children &&
              option.children.length > 0 ? (
                <Tooltip title={selectChildrenActionTitle} placement="top">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`${isSubtreeSelected ? 'Deselect' : 'Select'} ${option.name} and all children`}
                      disabled={!option.permissions?.write}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        if (isSubtreeSelected) {
                          props.onDeselectWithChildren?.(option)
                          return
                        }
                        props.onSelectWithChildren?.(option)
                      }}
                      sx={{
                        ml: 1,
                        flexShrink: 0,
                        color: isSelected ? 'secondary.main' : 'text.secondary',
                        backgroundColor: isSelected ? 'secondary.light' : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected ? 'secondary.light' : 'action.hover',
                        },
                      }}
                    >
                      <AccountTreeOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
          </ListItem>
        )
      }}
    />
  )
}

export default FoldersTreeAutocomplete
